import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLicense } from '../contexts/LicenseContext';
import { Home, Users, Clock, UserCircle2, FileText, DollarSign, MapPin, Calculator, Calendar, Package, BarChart3, Settings as SettingsIcon, LogOut, User, Info, Menu, X, AlertCircle, Key, Receipt, ClipboardCheck, Car } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import LicenseModal from './LicenseModal';
import SyncIndicator from './SyncIndicator';
import syncService from '../services/SyncService';
import autoSync from '../services/AutoSync';
import realtimeService from '../services/RealtimeService';
import tokenStore from '../services/tokenStore';
import inspectionsApi from '../services/inspectionsApi';
import { useData } from '../contexts/DataContext';
import { useElectronNavigation } from '../hooks/useElectron';
import useScrollbarAutohide from '../hooks/useScrollbarAutohide';

const Layout = () => {
  const { user, logout } = useAuth();
  const { isTrialMode, trialDaysRemaining, needsActivation, isExpired } = useLicense();
  const location = useLocation();
  const navigate = useNavigate();
  const [userPhoto, setUserPhoto] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [pendingInspections, setPendingInspections] = useState(0);
  
  // Enable Electron menu navigation
  useElectronNavigation();
  useScrollbarAutohide();

  // Fetch pending inspection count
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!user?.id) return;
      try {
        const response = await inspectionsApi.list(user.id, { status: 'pending' });
        if (response.success) {
          setPendingInspections(response.count);
        }
      } catch (error) {
        console.error('Error fetching pending inspections:', error);
      }
    };

    fetchPendingCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    // Load user photo from employees data
    if (user?.id) {
      const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
      const currentEmployee = employees.find(e => e.id === user.id);
      if (currentEmployee?.photo) {
        setUserPhoto(currentEmployee.photo);
      }
      
      // Initialize sync service with user ID
      syncService.initialize(user.id);
      syncService.loadQueue();
      
      // Install AutoSync (transparent localStorage <-> cloud bridge)
      autoSync.install();
      
      // Try to process any queued syncs
      if (navigator.onLine) {
        syncService.processSyncQueue();

        // Auto-join team via pre-approved email (if user has no workspace yet
        // AND their email was added to an owner's Employee Management list).
        const hasWorkspace = Boolean(localStorage.getItem('maidly_workspace_id'));
        const userEmail = user.email;
        const tryAutoJoinThenSync = async () => {
          const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
          // First: if localStorage is empty (e.g. logging in on a freshly
          // published domain, or in a new browser), try to restore the
          // workspace_id straight from the server via workspace_members.
          // This is critical for OWNERS — their email is not in team_invites
          // (they created the workspace), so the check-invite path below
          // never restores their workspace and their data appears "lost".
          if (!hasWorkspace && user?.id) {
            try {
              const authToken = tokenStore.getAccessToken();
              const resp = await fetch(
                `${apiUrl}/api/team/my-workspaces/${encodeURIComponent(user.id)}`,
                authToken
                  ? { headers: { Authorization: `Bearer ${authToken}` } }
                  : undefined,
              );
              const data = await resp.json();
              const ws = data?.workspaces?.[0];
              if (ws?.workspace_id) {
                syncService.setWorkspaceId(ws.workspace_id);
                if (ws.role) {
                  const stored = JSON.parse(localStorage.getItem('maidly_current_user') || '{}');
                  if (stored && stored.role !== ws.role) {
                    localStorage.setItem(
                      'maidly_current_user',
                      JSON.stringify({ ...stored, role: ws.role })
                    );
                  }
                }
                console.log('[Layout] Restored workspace from server:', ws.workspace_id);
              }
            } catch (e) {
              console.warn('[Layout] my-workspaces lookup failed:', e?.message || e);
            }
          }
          // Owner provisioning: if this user created an org but has no sync
          // workspace yet (normal signup path), create one now + migrate their
          // private data. Non-owners get workspace_id=null and fall through to
          // the invite path below.
          if (!localStorage.getItem('maidly_workspace_id') && user?.id) {
            try {
              const authToken = tokenStore.getAccessToken();
              const resp = await fetch(`${apiUrl}/api/team/ensure-workspace`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                },
                body: '{}',
              });
              const data = await resp.json();
              if (data?.workspace_id) {
                syncService.setWorkspaceId(data.workspace_id);
                console.log('[Layout] Ensured owner workspace:', data.workspace_id,
                  data.created ? `(created, migrated ${data.migrated} records)` : '(existing)');
              }
            } catch (e) {
              console.warn('[Layout] ensure-workspace failed:', e?.message || e);
            }
          }
          // Second: email-pre-approval path (for invited employees whose
          // workspace_members row doesn't exist yet on this device).
          const stillNoWorkspace = !localStorage.getItem('maidly_workspace_id');
          if (stillNoWorkspace && userEmail) {
            try {
              const ciToken = tokenStore.getAccessToken();
              const resp = await fetch(
                `${apiUrl}/api/team/check-invite/${encodeURIComponent(userEmail)}`,
                ciToken
                  ? { headers: { Authorization: `Bearer ${ciToken}` }, credentials: 'include' }
                  : { credentials: 'include' }
              );
              const data = await resp.json();
              if (data?.found && data?.workspace_id) {
                syncService.setWorkspaceId(data.workspace_id);
                // Update role to whatever the owner pre-assigned
                if (data.role) {
                  const stored = JSON.parse(localStorage.getItem('maidly_current_user') || '{}');
                  if (stored && stored.role !== data.role) {
                    localStorage.setItem(
                      'maidly_current_user',
                      JSON.stringify({ ...stored, role: data.role })
                    );
                  }
                }
                // Register membership for sync authorization (idempotent upsert)
                try {
                  const authToken = tokenStore.getAccessToken();
                  await fetch(`${apiUrl}/api/team/register-member`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                    },
                    body: JSON.stringify({
                      workspace_id: data.workspace_id,
                      user_id: user.id,
                      email: userEmail,
                      role: data.role || 'employee',
                      joined_via: 'email_auto',
                    }),
                  });
                } catch (e) {
                  console.warn('[Layout] register-member failed:', e);
                }
                console.log('[Layout] Auto-joined team workspace:', data.workspace_id);
              }
            } catch (e) {
              console.warn('[Layout] team-invite lookup failed:', e?.message || e);
            }
          }
          // Pull cloud data so this device shows what was created elsewhere,
          // then push anything we have locally that's not yet in the cloud.
          await autoSync.pullFromCloud();
          await autoSync.pushAll();
          // Open the realtime WebSocket so this device receives push events
          // when any other admin/manager/employee in the workspace mutates
          // data. Must be after auto-join so syncKey is final.
          realtimeService.start();
        };
        tryAutoJoinThenSync();
      }
    }
  }, [user]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    realtimeService.disconnect();
    logout();
    navigate('/login');
  };

  const isEmployee = user?.role === 'employee';

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard', tint: '#0073ea', roles: ['admin', 'manager', 'employee', 'owner'] },
    { path: '/employees', icon: Users, label: 'Employees', tint: '#a855f7', roles: ['admin', 'manager', 'owner'] },
    { path: '/time-tracking', icon: Clock, label: 'Time Clock', tint: '#3b82f6', roles: ['admin', 'manager', 'employee', 'owner'] },
    { path: '/customers', icon: UserCircle2, label: 'Customers', tint: '#ec4899', roles: ['admin', 'manager', 'owner'] },
    { path: '/scheduling', icon: Calendar, label: 'Scheduling', tint: '#f97316', roles: ['admin', 'manager', 'owner'] },
    { path: '/inspections', icon: ClipboardCheck, label: 'Inspections', tint: '#10b981', roles: ['admin', 'manager', 'employee', 'owner'] },
    { path: '/invoices', icon: DollarSign, label: 'Invoices', tint: '#22c55e', roles: ['admin', 'manager', 'owner'] },
    { path: '/expenses', icon: Receipt, label: 'Expenses', tint: '#ef4444', roles: ['admin', 'manager', 'owner'] },
    { path: '/mileage', icon: Car, label: 'Mileage', tint: '#0ea5e9', roles: ['admin', 'manager', 'employee', 'owner'] },
    { path: '/calculator', icon: Calculator, label: 'Calculator', tint: '#f59e0b', roles: ['admin', 'manager', 'owner'] },
    { path: '/inventory', icon: Package, label: 'Inventory', tint: '#8b5cf6', roles: ['admin', 'manager', 'owner'] },
    { path: '/settings', icon: SettingsIcon, label: 'Settings', tint: '#64748b', roles: ['admin', 'manager', 'owner'] },
  ];

  const bottomMenuItems = [
    { path: '/about', icon: Info, label: 'About', tint: '#0ea5e9', roles: ['admin', 'manager', 'employee'] },
  ];

  // Memoize role-filtering — these arrays change only when the user's role
  // changes, but the parent re-renders on every route change.
  const filteredMenuItems = useMemo(
    () => menuItems.filter(item => item.roles.includes(user?.role)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.role],
  );
  const filteredBottomMenuItems = useMemo(
    () => bottomMenuItems.filter(item => item.roles.includes(user?.role)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.role],
  );

  return (
    <div className="dashboard-layout">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button 
          className="mobile-menu-button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div
          className="mobile-logo"
          style={{ display: 'flex', alignItems: 'center', gap: 10 }}
        >
          <img
            src="/icon.svg"
            alt="Maidly Business Manager"
            data-testid="mobile-brand-logo"
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.32)',
              flexShrink: 0,
            }}
          />
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0073ea' }}>Maidly</div>
            <div style={{
              fontSize: '0.58rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: '#64748b',
              textTransform: 'uppercase',
            }}>
              Business Manager
            </div>
          </div>
        </div>
        <div style={{ width: '40px' }}></div> {/* Spacer for centering */}
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`sidebar ${mobileMenuOpen ? 'sidebar-open' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Header with Logo and User Profile */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <img
              src="/icon.svg"
              alt="Maidly Business Manager"
              data-testid="sidebar-brand-logo"
              style={{
                width: 128,
                height: 128,
                display: 'block',
                margin: '0 auto',
                borderRadius: 22,
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.28)',
              }}
            />
          </div>
          
          {/* User Profile Card */}
          <div style={{ 
            padding: '0.875rem', 
            background: '#f9fafb', 
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={user?.name}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #0073ea'
                  }}
                />
              ) : (
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}>
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'capitalize' }}>
                  {user?.role}
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                width: '100%',
                padding: '0.5rem',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                color: '#e44258',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e44258';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
                e.currentTarget.style.color = '#e44258';
              }}
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
        
        {/* Navigation Menu */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '1rem 0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            {filteredMenuItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1.5rem',
                    color: isActive ? '#0073ea' : '#6b7280',
                    backgroundColor: isActive ? '#e0f2ff' : 'transparent',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 400,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#f9fafb')}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: item.tint || '#64748b',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={16} strokeWidth={2.25} />
                  </span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.path === '/inspections' && pendingInspections > 0 && (
                    <span 
                      data-testid="pending-inspections-badge"
                      style={{
                        backgroundColor: '#ef4444',
                        color: 'white',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        padding: '2px 6px',
                        borderRadius: '9999px',
                        minWidth: '18px',
                        textAlign: 'center'
                      }}
                    >
                      {pendingInspections > 99 ? '99+' : pendingInspections}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          
          {/* Bottom Menu Items */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
            {filteredBottomMenuItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1.5rem',
                    color: isActive ? '#0073ea' : '#6b7280',
                    backgroundColor: isActive ? '#e0f2ff' : 'transparent',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 400,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#f9fafb')}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: item.tint || '#64748b',
                    color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={16} strokeWidth={2.25} />
                  </span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </aside>
      
      <main className="main-content" style={{ padding: '2rem' }}>
        {/* License Warning Banner */}
        {(isTrialMode && trialDaysRemaining <= 7 && trialDaysRemaining > 0) && (
          <div style={{
            padding: '1rem 1.5rem',
            background: trialDaysRemaining <= 3 ? '#fff4de' : '#e0f2ff',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
              <AlertCircle size={24} color={trialDaysRemaining <= 3 ? '#946800' : '#0073ea'} />
              <div>
                <div style={{ fontWeight: 600, color: trialDaysRemaining <= 3 ? '#946800' : '#0073ea' }}>
                  Trial Expires in {trialDaysRemaining} {trialDaysRemaining === 1 ? 'Day' : 'Days'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Activate your license to ensure uninterrupted access
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowLicenseModal(true)}
              style={{
                padding: '0.625rem 1.25rem',
                background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              <Key size={16} />
              Activate License
            </button>
          </div>
        )}

        {/* Trial Expired Banner */}
        {needsActivation && (
          <div style={{
            padding: '1.5rem',
            background: '#ffdbde',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
            border: '2px solid #e44258'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
              <AlertCircle size={24} color="#c1003d" />
              <div>
                <div style={{ fontWeight: 700, color: '#c1003d', fontSize: '1.1rem' }}>
                  Trial Period Expired
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Please activate a license key to continue using Maidly Business Manager
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowLicenseModal(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#e44258',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem'
              }}
            >
              <Key size={18} />
              Activate Now
            </button>
          </div>
        )}

        <Outlet />
      </main>

      {/* License Modal */}
      <LicenseModal 
        isOpen={showLicenseModal || needsActivation}
        onClose={() => setShowLicenseModal(false)}
        canClose={!needsActivation}
      />
    </div>
  );
};

export default Layout;
